import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
} from '@azure/msal-browser';

/**
 * Signing in as an author.
 *
 * The platform embeds this app with a token that describes a *report*. Someone
 * who opens the builder directly has no such token and no way to get one except
 * by going and looking at a report first — which is the friction this replaces.
 *
 * Ships dark: with no client id configured, `enabled()` is false, nothing is
 * constructed, and the app behaves exactly as it did before. That matters,
 * because the embed paths must keep working on environments that never get a
 * registration.
 */

const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID as string | undefined;
const AUTHORITY = import.meta.env.VITE_ENTRA_AUTHORITY as string | undefined;
/** The API scope this app asks for, exposed by its own registration. */
const SCOPE = CLIENT_ID ? `api://${CLIENT_ID}/access_as_user` : '';

export const enabled = (): boolean => Boolean(CLIENT_ID && AUTHORITY);

// Memoised as the INITIALISED-client PROMISE, not the instance. The studio's
// first load fires several API calls at once, each of which asks for a token,
// so client() is called concurrently before the first init has resolved.
// Guarding on the instance handed the later callers an uninitialised
// PublicClientApplication — MSAL then threw uninitialized_public_client_
// application on the very next call. Guarding on the promise makes every caller
// await the same initialize()/handleRedirectPromise() and never see a
// half-built client.
let clientPromise: Promise<PublicClientApplication> | null = null;

const client = (): Promise<PublicClientApplication> => {
  if (!clientPromise) {
    clientPromise = (async () => {
      const app = new PublicClientApplication({
        auth: {
          clientId: CLIENT_ID as string,
          authority: AUTHORITY as string,
          // MSAL trusts login.microsoftonline.com implicitly; listing the host
          // is harmless and keeps this correct if the authority ever moves to a
          // CIAM domain whose sign-in host it does not know.
          knownAuthorities: [new URL(AUTHORITY as string).host],
          redirectUri: window.location.origin,
          postLogoutRedirectUri: window.location.origin,
          navigateToLoginRequestUrl: false,
        },
        cache: {
          // Session storage, not local: an authoring session ends with the tab.
          // A longer leash than the in-memory embed session, which is the point,
          // but it should not outlive the browser window.
          cacheLocation: 'sessionStorage',
        },
      });
      await app.initialize();
      // Completes a redirect we started; a no-op on a normal load. Runs once,
      // inside the shared promise, so it is not raced either.
      const redirect = await app.handleRedirectPromise();
      // Make the just-signed-in account active explicitly. Relying on
      // getAllAccounts()[0] alone is what let a returning redirect look
      // signed-out — and then bounce straight back to the login screen.
      const account = redirect?.account ?? app.getActiveAccount() ?? app.getAllAccounts()[0] ?? null;
      if (account) app.setActiveAccount(account);
      return app;
    })().catch((err) => {
      // A failed init must not be cached, or every later call reuses the
      // rejection and sign-in can never recover without a reload.
      clientPromise = null;
      throw err;
    });
  }
  return clientPromise;
};

const active = (msal: PublicClientApplication): AccountInfo | null =>
  msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;

/**
 * An access token for this app's API, or null if nobody is signed in.
 *
 * Silent first. Falling back to a redirect on every call would send someone to
 * the sign-in page mid-edit; the caller decides when interaction is acceptable.
 */
export const getToken = async (): Promise<string | null> => {
  if (!enabled()) return null;

  const msal = await client();
  const account = active(msal);
  if (!account) return null;

  try {
    const result = await msal.acquireTokenSilent({ account, scopes: [SCOPE] });
    return result.accessToken;
  } catch (err) {
    // Consent or a fresh sign-in is needed — the caller's cue to offer a
    // button, not something to force here.
    if (err instanceof InteractionRequiredAuthError) return null;
    throw err;
  }
};

/** Sends the browser to the sign-in page. Returns via the redirect URI. */
export const signIn = async (): Promise<void> => {
  const msal = await client();
  await msal.loginRedirect({ scopes: [SCOPE] });
};

export const signOut = async (): Promise<void> => {
  const msal = await client();
  const account = active(msal);
  await msal.logoutRedirect(account ? { account } : undefined);
};

/** Who is signed in, for showing it in the header. */
export const currentAccount = async (): Promise<AccountInfo | null> => {
  if (!enabled()) return null;
  return active(await client());
};
